import { Link } from '@mui/material';

interface SkipNavigationProps {
  /** DOM id of the main content element this link skips to (e.g., 'main-content'). */
  readonly targetId: string;
}

/**
 * Visually-hidden-until-focused skip-navigation link per WCAG 2.4.1
 * (Bypass Blocks) and public-website design §7.
 *
 * Rendered as the first focusable element by `<AppShell>` so keyboard
 * + screen-reader users can jump past the chrome (AppBar + Drawer)
 * straight to the page's main content. The link is positioned off-
 * screen by default and slides into the top-left corner on focus.
 *
 * Implementation: a plain MUI `<Link>` styled with the standard "sr-
 * only until focused" pattern. The link's destination is
 * `#${targetId}` — the consumer (AppShell) puts that id on its
 * `<main>` element.
 *
 * Note on the LinkBehavior bridge: this component renders with
 * `component="a"` so the fragment-only `href` (`#main-content`) bypasses
 * the `LinkBehavior` adapter (which maps `href` → react-router
 * `<Link to>`). Skip-nav is an in-page anchor jump, not a route change,
 * so a plain `<a>` is the correct WCAG 2.4.1 (Bypass Blocks)
 * implementation.
 */
export function SkipNavigation({ targetId }: SkipNavigationProps): JSX.Element {
  return (
    <Link
      href={`#${targetId}`}
      component="a"
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        padding: 1,
        background: 'background.paper',
        color: 'text.primary',
        zIndex: 9999,
        // Visually hide off-screen until focused.
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: 1,
        width: 1,
        margin: -1,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        '&:focus, &:focus-visible': {
          // Reveal: undo the visually-hidden positioning on focus.
          clip: 'auto',
          clipPath: 'none',
          height: 'auto',
          width: 'auto',
          margin: 0,
          overflow: 'visible',
          whiteSpace: 'normal',
          outline: '2px solid',
          outlineColor: 'primary.main',
        },
      }}
    >
      Skip to main content
    </Link>
  );
}
